import React, { useState, useEffect } from "react";
import {
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiAlertCircle,
  FiFilter,
  FiRefreshCw,
  FiBookOpen,
} from "react-icons/fi";
import api from "../../../../connector/api";

export default function StudentAttendanceTab({ student }) {
  const [attendanceCodes, setAttendanceCodes] = useState([]);
  const [attendanceRows, setAttendanceRows] = useState([]);
  const [filterRange, setFilterRange] = useState("day"); // Default: 'day'
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!student?.id) return;

    const fetchAttendanceData = async () => {
      setLoading(true);
      try {
        // 1. Fetch Legend Codes
        const codesRes = await api.get("/settings/attendance/code/");
        const fetchedCodes = Array.isArray(codesRes.data)
          ? codesRes.data
          : codesRes.data.results || [];
        setAttendanceCodes(fetchedCodes);

        // 2. Fetch Lightweight Classroom Attendance Endpoint
        const attendanceRes = await api.get(
          `/students/${student.id}/classroom-attendance/?range=${filterRange}`
        );
        setAttendanceRows(attendanceRes.data || []);
      } catch (err) {
        console.error("Failed to load attendance records:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAttendanceData();
  }, [student?.id, filterRange]);

  // Helper for status badge styling
  const getBadgeStyle = (codeDetail) => {
    if (codeDetail?.is_present) {
      return { bg: "bg-emerald-50 text-emerald-700 border-emerald-200/60", icon: FiCheckCircle };
    }
    if (codeDetail?.is_absent) {
      return { bg: "bg-red-50 text-red-700 border-red-200/60", icon: FiXCircle };
    }
    if (codeDetail?.is_tardy) {
      return { bg: "bg-amber-50 text-amber-700 border-amber-200/60", icon: FiClock };
    }
    return { bg: "bg-blue-50 text-blue-700 border-blue-200/60", icon: FiAlertCircle };
  };

  return (
    <div className="space-y-4 text-xs animate-in fade-in duration-150">
      {/* Top Controls: Title & Timeframe Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-1 border-b border-zinc-100">
        <h3 className="font-bold text-zinc-900 text-xs flex items-center gap-1.5">
          <FiBookOpen className="w-3.5 h-3.5 text-zinc-500" /> Student Attendance
        </h3>

        <div className="flex items-center gap-1 bg-zinc-100/80 p-1 rounded-lg border border-zinc-200/60 text-[11px] font-medium self-start sm:self-auto">
          <span className="px-2 text-zinc-400 flex items-center gap-1">
            <FiFilter className="w-3 h-3" /> Filter:
          </span>
          {[
            { id: "day", label: "Day" },
            { id: "week", label: "Week" },
            { id: "month", label: "Month" },
            { id: "term", label: "Term" },
          ].map((range) => (
            <button
              key={range.id}
              onClick={() => setFilterRange(range.id)}
              className={`px-2.5 py-1 rounded-md transition-all cursor-pointer capitalize ${
                filterRange === range.id
                  ? "bg-white text-zinc-900 font-bold shadow-2xs"
                  : "text-zinc-500 hover:text-zinc-800"
              }`}
            >
              {range.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main Container: Codes Legend Header + 3-Column Table */}
      <div className="border border-zinc-200/80 rounded-lg overflow-hidden bg-white shadow-2xs">
        {/* Attendance Codes Legend Banner (Placed directly on top of table) */}
        <div className="p-3 bg-zinc-50/90 border-b border-zinc-200/80 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="flex flex-wrap items-center gap-1.5">
              {attendanceCodes.map((item) => {
                const style = getBadgeStyle(item);
                return (
                  <span
                    key={item.id || item.code}
                    className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-bold border ${style.bg}`}
                    title={item.description}
                  >
                    {item.code} <span className="font-normal opacity-80 ml-1">({item.description})</span>
                  </span>
                );
              })}
            </div>
          </div>

          {loading && (
            <span className="text-[11px] text-zinc-400 flex items-center gap-1 shrink-0">
              <FiRefreshCw className="w-3 h-3 animate-spin" /> Syncing...
            </span>
          )}
        </div>

        {/* 3-Column Subject Attendance Table */}
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-zinc-50/70 border-b border-zinc-200/80 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">
              <th className="py-2.5 px-4 w-1/3">Subject Name</th>
              <th className="py-2.5 px-4 w-1/4">Attendance Code</th>
              <th className="py-2.5 px-4">Comment / Remarks</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100 text-xs text-zinc-700">
            {loading ? (
              <tr>
                <td colSpan="3" className="py-8 text-center text-zinc-400">
                  <FiRefreshCw className="w-4 h-4 animate-spin inline mr-2" />
                  Loading subject records...
                </td>
              </tr>
            ) : attendanceRows.length === 0 ? (
              <tr>
                <td colSpan="3" className="py-8 text-center text-zinc-400">
                  No subjects registered in this student's classroom.
                </td>
              </tr>
            ) : (
              attendanceRows.map((row) => {
                const codeDetail = row.attendance_code;
                const style = getBadgeStyle(codeDetail);
                const Icon = style.icon;

                return (
                  <tr
                    key={row.subject_assignment_id}
                    className="hover:bg-zinc-50/60 transition-colors"
                  >
                    {/* Column 1: Subject Name */}
                    <td className="py-2.5 px-4 font-semibold text-zinc-800">
                      {row.subject_name}
                    </td>

                    {/* Column 2: Code (Empty '—' if unrecorded) */}
                    <td className="py-2.5 px-4">
                      {codeDetail ? (
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${style.bg}`}
                        >
                          <Icon className="w-3 h-3" />
                          {codeDetail.code} ({codeDetail.description})
                        </span>
                      ) : (
                        <span className="text-zinc-300 font-mono text-[11px]">—</span>
                      )}
                    </td>

                    {/* Column 3: Comment */}
                    <td className="py-2.5 px-4 text-zinc-500">
                      {row.remarks ? (
                        row.remarks
                      ) : (
                        <span className="text-zinc-300">—</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}